/*
 * Copyright (c) 2021. DDMIX - All Rights Reserved.
 * This source code can not be copied and/or distributed without the express permission of DDMIX.
 *
 * Written by Jake Lee <leejaku@gmail.com>
 */

import { useState } from 'react';

/**
 * Cookie 우선 사용하고, 없으면 LocalStorage를 뒤지는 hook임. 
 * set 함수는 local storage만 적용됨.
 * 이게 왜 필요하나면, 개발환경에서는 http://localhost:3000 으로 접속하고, 실제로는 8383 포트로 요청하는데
 * 이렇게 하면 cookie가 8383 포트에 생겨서 3000번 포트에서 작동하는 웹코드에서는 cookie가 보이지 않음. 
 * 그래서 일단 Cookie에서 읽고, 안되면 localStorage에서 읽는 것임. 
 * 
 * @param {*} key 
 * @param {*} initialValue 
 * @returns 
 */
export function useCookieStorage(key, initialValue) {
    // State to store our value
    // Pass initial state function to useState so logic is only executed once
    const [storedValue, setStoredValue] = useState(() => {
      if (typeof window === "undefined") {
        return initialValue;
      }
      try {
        // Cookie에서 먼저 가져와본다. 
        let cookie = getCookie(key);
        if (cookie) {
            return cookie;
        } else {
            // Get from local storage by key
            const item = window.localStorage.getItem(key);
            // Parse stored json or if none return initialValue
            return item || initialValue;
        }
      } catch (error) {
        // If error also return initialValue
        console.log(error);
        return initialValue;
      }
    });
    // Return a wrapped version of useState's setter function that ...
    // ... persists the new value to localStorage.
    const setValue = (value) => {
      try {
        // Allow value to be a function so we have same API as useState
        const valueToStore =
          value instanceof Function ? value(storedValue) : value;
        // Save state
        setStoredValue(valueToStore);
        // Save to local storage
        if (typeof window !== "undefined") {
          window.localStorage.setItem(key, valueToStore);
        }
      } catch (error) {
        // A more advanced implementation would handle the error case
        console.log(error);
      }
    };
    return [storedValue, setValue];
  }
  
// 주어진 이름의 쿠키를 반환하는데,
// 조건에 맞는 쿠키가 없다면 undefined를 반환합니다.
function getCookie(name) {
  let matches = document.cookie.match(new RegExp(
    "(?:^|; )" + name.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, '\\$1') + "=([^;]*)"
  ));
  return matches ? decodeURIComponent(matches[1]) : undefined;
}

/*
function getCookie(cookie_name) {
    var x, y;
    var val = document.cookie.split(';');
    for (var i = 0; i < val.length; i++) {
        x = val[i].substring(0, val[i].indexOf('='));
        y = val[i].substring(val[i].indexOf('=') + 1);
        x = x.replace(/^\s+|\s+$/g, '');
        // 앞과 뒤의 공백 제거하기
        if (x == cookie_name) {
            //return unescape(y);
            return decodeURI(y);  // 또는 decodeURIComponent 어느것을 사용해야 할지????
            // unescape로 디코딩 후 값 리턴
        }
    }
}
*/

/**
 * example: setCookie('user', 'John', {secure: true, 'max-age': 3600});
 * @param {} name 
 * @param {*} value 
 * @param {*} options 
 */
function setCookie(name, value, options = {}) {
  options = {
    path: '/',
    // 필요한 경우, 옵션 기본값을 설정할 수도 있습니다.
    ...options
  };

  if (options.expires instanceof Date) {
    options.expires = options.expires.toUTCString();
  }

  let updatedCookie = encodeURIComponent(name) + "=" + encodeURIComponent(value);

  for (let optionKey in options) {
    updatedCookie += "; " + optionKey;
    let optionValue = options[optionKey];
    if (optionValue !== true) {
      updatedCookie += "=" + optionValue;
    }
  }

  document.cookie = updatedCookie;
}

function deleteCookie(name) {
  setCookie(name, "", {
    'max-age': -1
  })
}
